import { FC, useState } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { PdpRespStorytellingText_storyTellingTab } from './__generated__/PdpRespStorytellingText_storyTellingTab.graphql';
import { fireReadMoreTracking } from '../PdpSharedStorytellingComponents/pdpSharedStorytellingHelpers';
import styles from '../PdpSharedStorytellingComponents/PdpSharedStorytellingText.scss';

type Props = {
    storyTellingTab: PdpRespStorytellingText_storyTellingTab;
};

const PdpRespStorytellingTextComponent: FC<Props> = ({ storyTellingTab }) => {
    const {
        articleLink,
        browseLink,
        tabTitle,
        contentHeader,
        contentCreatorAttribution,
        contentBodyText,
        tabType,
    } = storyTellingTab;
    const [isContentExpandable, setIsContentExpandable] = useState(false);

    const readStoryLink = articleLink && (
        <Link href={articleLink} target="_blank">
            <FormattedMessage
                defaultMessage="Read the full story"
                id="pdp.resp.storytelling.readStoryLink"
            />
        </Link>
    );

    const readMoreBrowseLink = browseLink && (
        <SeoLink
            linkData={browseLink}
            dataTn="pdp-storytelling-readmore"
            onClick={() => fireReadMoreTracking({ tabType })}
        >
            <FormattedMessage defaultMessage="Read more" id="pdp.resp.storytelling.readMoreLink" />
        </SeoLink>
    );

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    <Heading className={styles.tabTitle}>{tabTitle}</Heading>
                    <div className={styles.contentHeaderWrapper}>
                        <Heading
                            className={styles.contentHeader}
                            dangerouslySetInnerHTML={{ __html: contentHeader }}
                        />
                    </div>
                    {contentCreatorAttribution && (
                        <div className={styles.creatorAttribution}>
                            <FormattedMessage
                                defaultMessage="Designed by {creator}"
                                id="pdp.resp.storytelling.creatorAttribution"
                                values={{
                                    creator: (
                                        <SeoLink
                                            linkData={contentCreatorAttribution.creatorBrowseUrl}
                                            dataTn="pdp-storytelling-design-attribution-link"
                                            onClick={() => {
                                                trackEvent({
                                                    category: 'promo interaction',
                                                    action: 'iconic design creator clicked',
                                                    label: 'pdp',
                                                });
                                            }}
                                        >
                                            {contentCreatorAttribution?.creatorName}
                                        </SeoLink>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    <ExpandingArea
                        expanded={false}
                        showGradient={false}
                        setIsContentExpandable={setIsContentExpandable}
                        collapsedHeight={120}
                        expandingAreaClass={styles.contentBodyWrapper}
                    >
                        <div
                            className={styles.contentBodyText}
                            dangerouslySetInnerHTML={{ __html: contentBodyText }}
                        />
                    </ExpandingArea>
                    {isContentExpandable && readMoreBrowseLink}
                    {readStoryLink}
                </>
            )}
        </HeadingLevel>
    );
};

export const PdpRespStorytellingText = createFragmentContainer(PdpRespStorytellingTextComponent, {
    storyTellingTab: graphql`
        fragment PdpRespStorytellingText_storyTellingTab on StoryTellingTab {
            tabTitle
            contentHeader
            contentCreatorAttribution {
                creatorName
                creatorBrowseUrl {
                    ...SeoLink_linkData
                }
            }
            contentBodyText
            articleLink
            browseLink {
                ...SeoLink_linkData
            }
            tabType
        }
    `,
});
